import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  Bars3Icon
} from '@heroicons/react/24/outline';

const ENTITY_TYPES = [
  { value: 'plot', label: 'Plots' },
  { value: 'contact', label: 'Contacts' },
  { value: 'sale', label: 'Sales' }
];

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'checkbox', label: 'Checkbox' }
];

const FlexFieldsConfig = ({ embedded = false }) => {
  const { token } = useContext(AppContext);
  const [definitions, setDefinitions] = useState([]);
  const [activeTab, setActiveTab] = useState('plot');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    fieldName: '',
    fieldLabel: '',
    fieldType: 'text',
    options: [],
    isRequired: false,
    isPublic: false,
    sortOrder: 0
  });
  const [optionInput, setOptionInput] = useState('');
  const [draggedId, setDraggedId] = useState(null);

  useEffect(() => {
    fetchDefinitions();
  }, []);

  const fetchDefinitions = async () => {
    try {
      const response = await fetch('/api/flex-fields/definitions', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setDefinitions(await response.json());
      }
    } catch (error) {
      console.error('Error fetching definitions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.fieldLabel) return;

    setIsLoading(true);
    try {
      const fieldName = formData.fieldName || formData.fieldLabel.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
      const body = { ...formData, fieldName, entityType: activeTab };

      const url = editingId
        ? `/api/flex-fields/definitions/${editingId}`
        : '/api/flex-fields/definitions';

      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        await fetchDefinitions();
        resetForm();
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to save field');
      }
    } catch (error) {
      console.error('Error saving field:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (def) => {
    setFormData({
      fieldName: def.fieldName,
      fieldLabel: def.fieldLabel,
      fieldType: def.fieldType,
      options: def.options || [],
      isRequired: def.isRequired,
      isPublic: def.isPublic,
      sortOrder: def.sortOrder
    });
    setEditingId(def.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this field? All stored values will be removed.')) return;
    try {
      await fetch(`/api/flex-fields/definitions/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchDefinitions();
    } catch (error) {
      console.error('Error deleting field:', error);
    }
  };

  const addOption = () => {
    if (!optionInput.trim()) return;
    setFormData(prev => ({ ...prev, options: [...prev.options, optionInput.trim()] }));
    setOptionInput('');
  };

  const removeOption = (idx) => {
    setFormData(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== idx) }));
  };

  const resetForm = () => {
    setFormData({ fieldName: '', fieldLabel: '', fieldType: 'text', options: [], isRequired: false, isPublic: false, sortOrder: 0 });
    setEditingId(null);
    setShowForm(false);
    setOptionInput('');
  };

  const handleDragStart = (id) => {
    setDraggedId(id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (targetId) => {
    if (draggedId === targetId) { setDraggedId(null); return; }

    const filtered = definitions.filter(d => d.entityType === activeTab);
    const draggedIdx = filtered.findIndex(d => d.id === draggedId);
    const targetIdx = filtered.findIndex(d => d.id === targetId);
    if (draggedIdx === -1 || targetIdx === -1) { setDraggedId(null); return; }

    // Reorder the array
    const reordered = [...filtered];
    const [moved] = reordered.splice(draggedIdx, 1);
    reordered.splice(targetIdx, 0, moved);

    // Update sort orders
    const orders = reordered.map((def, idx) => ({ id: def.id, sortOrder: idx }));

    // Optimistic update
    const updatedDefs = definitions.map(d => {
      const order = orders.find(o => o.id === d.id);
      return order ? { ...d, sortOrder: order.sortOrder } : d;
    });
    setDefinitions(updatedDefs);
    setDraggedId(null);

    // Persist to backend
    try {
      await fetch('/api/flex-fields/definitions/reorder', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ orders })
      });
    } catch (error) {
      console.error('Error saving order:', error);
      fetchDefinitions();
    }
  };

  const filteredDefs = definitions.filter(d => d.entityType === activeTab);

  const content = (
    <>
      {/* Entity type tabs */}
      <div className="flex border-b mb-6">
        {ENTITY_TYPES.map(type => (
          <button
            key={type.value}
            onClick={() => setActiveTab(type.value)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === type.value
                ? 'border-brand-green-600 text-brand-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {type.label}
          </button>
        ))}
      </div>

      {/* Add button */}
      <div className="mb-4">
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-brand-green-600 text-white rounded-lg hover:bg-brand-green-700"
        >
          <PlusIcon className="h-4 w-4" />
          Add Field
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow p-4 mb-6 border">
          <h3 className="font-medium mb-3">{editingId ? 'Edit' : 'Add'} Field for {ENTITY_TYPES.find(t => t.value === activeTab)?.label}</h3>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Field Label</label>
                <input
                  type="text"
                  value={formData.fieldLabel}
                  onChange={e => setFormData(prev => ({ ...prev, fieldLabel: e.target.value }))}
                  placeholder="e.g., Lot Number"
                  required
                  className="w-full px-3 py-2 border rounded-md focus:ring-brand-green-500 focus:border-brand-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                <select
                  value={formData.fieldType}
                  onChange={e => setFormData(prev => ({ ...prev, fieldType: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-brand-green-500 focus:border-brand-green-500"
                >
                  {FIELD_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {formData.fieldType === 'select' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Options</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={optionInput}
                    onChange={e => setOptionInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }}
                    placeholder="Add an option"
                    className="flex-1 px-3 py-2 border rounded-md text-sm"
                  />
                  <button type="button" onClick={addOption} className="px-3 py-2 bg-gray-100 rounded-md text-sm hover:bg-gray-200">
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.options.map((opt, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md text-sm">
                      {opt}
                      <button type="button" onClick={() => removeOption(idx)} className="text-gray-400 hover:text-red-500">
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.isRequired}
                  onChange={e => setFormData(prev => ({ ...prev, isRequired: e.target.checked }))}
                  className="h-4 w-4 text-brand-green-600 rounded"
                />
                Required
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.isPublic}
                  onChange={e => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
                  className="h-4 w-4 text-brand-green-600 rounded"
                />
                Show on Public Portal
              </label>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm bg-brand-green-600 text-white rounded-md hover:bg-brand-green-700 disabled:bg-gray-300"
              >
                {isLoading ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </button>
              <button type="button" onClick={resetForm} className="px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Fields list */}
      <div className="bg-white rounded-lg shadow">
        {filteredDefs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No custom fields defined for {ENTITY_TYPES.find(t => t.value === activeTab)?.label}.</p>
            <p className="text-sm mt-1">Click "Add Field" to create one.</p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredDefs.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)).map(def => (
              <div
                key={def.id}
                draggable
                onDragStart={() => handleDragStart(def.id)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(def.id)}
                className={`flex items-center justify-between p-4 hover:bg-gray-50 cursor-grab active:cursor-grabbing ${draggedId === def.id ? 'opacity-50 bg-blue-50' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <Bars3Icon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{def.fieldLabel}</span>
                    <span className="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">
                      {FIELD_TYPES.find(t => t.value === def.fieldType)?.label}
                    </span>
                    {def.isRequired && (
                      <span className="text-xs px-2 py-0.5 bg-red-50 text-red-600 rounded-full">Required</span>
                    )}
                    {def.isPublic && (
                      <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full">Public</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Field name: {def.fieldName}
                    {def.options && def.options.length > 0 && ` | Options: ${def.options.join(', ')}`}
                  </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(def)}
                    className="p-1.5 text-gray-400 hover:text-brand-green-600 rounded"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(def.id)}
                    className="p-1.5 text-gray-400 hover:text-red-500 rounded"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  if (embedded) {
    return (
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900 mb-1">Custom Fields</h3>
          <p className="text-sm text-gray-500 mb-4">
            Define additional fields for plots, contacts, and sales.
          </p>
          {content}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Custom Fields</h1>
        <p className="text-gray-600">Define additional fields for plots, contacts, and sales.</p>
      </div>
      {content}
    </div>
  );
};

export default FlexFieldsConfig;
